import { createLens } from '@dhmk/zustand-lens';
import type { StateCreator } from 'zustand';
import type { EdgeId, NodeId, PortId } from '../types/graph';
import type { GraphState } from '.';

export type Position = {
  x: number;
  y: number;
};

export type GraphViewState = {
  nodePositions: Record<NodeId, Position>;
};

export enum NodePickerContextType {
  InsertOnEdge = 'insert_on_edge',
  AddChildToNode = 'add_child_to_node',
  ReplaceNode = 'replace_node',
}

export type NodePickerContext =
  | {
      mode: NodePickerContextType.InsertOnEdge;
      edgeId: EdgeId;
    }
  | {
      mode: NodePickerContextType.AddChildToNode;
      nodeId: NodeId;
      portId?: PortId;
    }
  | {
      mode: NodePickerContextType.ReplaceNode;
      nodeId: NodeId;
    };

export enum DrawerKind {
  None = 'none',
  NodePicker = 'node_picker',
  NodeConfig = 'node_config',
  GraphConfig = 'graph_config',
}

export type DrawerState =
  | { kind: DrawerKind.None }
  | { kind: DrawerKind.NodePicker; context: NodePickerContext }
  | { kind: DrawerKind.NodeConfig; nodeId: NodeId; tab?: string }
  | { kind: DrawerKind.GraphConfig; tab?: string };

export interface UISlice {
  view: GraphViewState;
  drawer: DrawerState;
  isLayoutRunning: boolean;
  focusNodeId: NodeId | null;

  setNodePositions: (positions: Record<NodeId, Position>) => void;
  setLayoutRunning: (running: boolean) => void;
  setFocusNodeId: (nodeId: NodeId | null) => void;

  openDrawer: (drawer: DrawerState) => void;
  closeDrawer: () => void;
  isDrawerOpen: (kind?: DrawerKind) => boolean;
}

const sameNodePositions = (
  left: Record<NodeId, Position>,
  right: Record<NodeId, Position>
) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((id) => {
    const leftPosition = left[id];
    const rightPosition = right[id];
    if (!leftPosition || !rightPosition) {
      return false;
    }
    return (
      leftPosition.x === rightPosition.x && leftPosition.y === rightPosition.y
    );
  });
};

export const createUISlice: StateCreator<
  GraphState,
  [['zustand/immer', never]],
  [],
  UISlice
> = (_set, _get) => {
  const [set, get] = createLens(_set, _get, ['ui']);

  return {
    view: {
      nodePositions: {},
    },
    drawer: {
      kind: DrawerKind.None,
    },
    isLayoutRunning: false,
    focusNodeId: null,

    setLayoutRunning: (running) => {
      set((s) => {
        s.isLayoutRunning = running;
      });
    },

    setFocusNodeId: (nodeId) => {
      set((s) => {
        s.focusNodeId = nodeId;
      });
    },

    setNodePositions: (positions) => {
      set((s) => {
        if (sameNodePositions(s.view.nodePositions, positions)) {
          return;
        }
        s.view.nodePositions = positions;
      });
    },

    openDrawer: (drawer) => {
      set((s) => {
        s.drawer = drawer;
      });
    },
    closeDrawer: () => {
      set((s) => {
        s.drawer = { kind: DrawerKind.None };
      });
    },
    isDrawerOpen: (kind) => {
      const current = get().drawer.kind;
      if (kind === undefined) {
        return current !== DrawerKind.None;
      }
      return current === kind;
    },
  };
};
